// src/components/resources/ebooks/EbooksContent.tsx

'use client'

import { Suspense } from 'react'
import Link from 'next/link'
import type { EbookHubSummary } from '~/lib/resources/data/ebooks/hub-summary'
import { getEbookCoverTheme } from '~/lib/resources/data/ebooks/themes'
import { EbookCover } from '~/components/resources/ebooks/EbookCover'
import ResourceGrid from '~/components/resources/ResourceGrid'
import { ResourceGridSkeleton } from '~/components/resources/ResourceGridSkeleton'
import { ArrowRightIcon, BookOpenIcon } from '@heroicons/react/24/outline'

interface EbooksContentProps {
  ebooks: EbookHubSummary[]
}

const TOPIC_CARDS = [
  {
    key: 'creative_strategy',
    title: 'Creative & Testing',
    description: 'Structured experiments, A/B tests, and learning loops across Meta, Google, and TikTok.',
    color: 'from-violet-500/10 to-purple-600/10 dark:from-violet-400/10 dark:to-purple-500/10',
  },
  {
    key: 'performance_marketing',
    title: 'Performance & ROAS',
    description: 'Margin-aware targets, efficiency diagnostics, and budget allocation frameworks.',
    color: 'from-emerald-500/10 to-green-600/10 dark:from-emerald-400/10 dark:to-green-500/10',
  },
  {
    key: 'analytics',
    title: 'Metrics & Reporting',
    description: 'KPI definitions, formulas, and benchmark context for cross-functional reporting.',
    color: 'from-blue-500/10 to-sky-600/10 dark:from-blue-400/10 dark:to-sky-500/10',
  },
] as const

// Non-interactive info panels — there is no per-topic hub or filtered URL to
// link to (only /resources/ebooks/[slug] exists), and anchor/query-param
// pseudo-links are the same false-affordance / GSC-duplicate trap documented
// in TemplatesContent's CategoriesGrid. So: no hover states, no link idiom.
function TopicsGrid(): JSX.Element {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {TOPIC_CARDS.map((topic) => {
        const theme = getEbookCoverTheme(topic.key)
        return (
          <div
            key={topic.key}
            className="relative overflow-hidden rounded-2xl bg-gray-50 p-6 dark:bg-gray-900/40"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${topic.color} opacity-60`} />
            <div className="relative">
              <span className="text-2xl text-gray-400 dark:text-gray-500">{theme.glyph}</span>
              <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">{topic.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{topic.description}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function FeaturedEbookSpotlight({ ebook }: { ebook: EbookHubSummary }): JSX.Element {
  const theme = getEbookCoverTheme(ebook.category)

  return (
    <Link
      href={`/resources/ebooks/${ebook.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 transition-all hover:border-blue-200 hover:shadow-xl dark:border-gray-800 dark:from-gray-900 dark:via-gray-950 dark:to-blue-950/20 dark:hover:border-blue-800 sm:flex-row"
    >
      <div className="flex items-center justify-center p-8 sm:w-2/5 sm:p-10">
        <EbookCover
          title={ebook.title}
          category={ebook.category}
          author={ebook.author}
          pageCount={ebook.pageCount}
          size="lg"
          elevated
          className="motion-safe:group-hover:-translate-y-2 motion-safe:transition-transform motion-safe:duration-300"
        />
      </div>
      <div className="flex flex-1 flex-col justify-center p-8 pt-0 sm:p-10 sm:pt-10">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
          <BookOpenIcon className="h-3.5 w-3.5" /> Featured
        </span>
        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {theme.label}
        </p>
        <h3 className="mt-1 text-2xl font-bold text-gray-900 group-hover:text-blue-700 dark:text-white dark:group-hover:text-blue-300">
          {ebook.title}
        </h3>
        <p className="mt-3 line-clamp-3 text-gray-600 dark:text-gray-300">{ebook.description}</p>
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-blue-400">
          Read preview & download <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}

export function EbooksContent({ ebooks }: EbooksContentProps): JSX.Element {
  const featured = ebooks.filter((e) => e.featured)
  const spotlight = featured[0]
  const featuredGrid = spotlight
    ? featured.filter((ebook) => ebook.slug !== spotlight.slug)
    : featured

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Hero.

          `isolate` is load-bearing: it pins a permanent stacking context so
          the -z-10 gradient blob below can't fall behind the opaque body
          background once the layout's mount fade finishes — the cause of the
          hero color flashing then vanishing (#561 `c4920797286`; see the
          matching comment in TemplatesContent for the full mechanism). */}
      <section className="relative isolate py-12 sm:py-16">
        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-indigo-500 to-violet-500 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>

        <div className="relative">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-6xl">
            Marketing eBooks
            <span className="block text-indigo-600 dark:text-indigo-400">Built to Download</span>
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-400">
            In-depth playbooks and handbooks for performance marketers. Browse chapter previews on-site,
            then get the full PDF delivered to your inbox — free.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              href="#ebooks"
              className="inline-flex items-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-400"
            >
              Browse eBooks
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            {/* "See topic overview" (not "Explore topics"): the #topics
                section is now flat informational panels with no per-topic
                links, so the CTA copy sets a browse-only expectation
                instead of promising interactive navigation
                (`#556` r3545922631). */}
            <Link
              href="#topics"
              className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              See topic overview
            </Link>
          </div>

          <dl className="mt-10 grid grid-cols-3 gap-6 border-t border-gray-200 pt-8 dark:border-gray-800 sm:max-w-lg">
            <div>
              <dt className="text-2xl font-bold text-gray-900 dark:text-white">{ebooks.length}</dt>
              <dd className="text-sm text-gray-500 dark:text-gray-400">eBooks</dd>
            </div>
            <div>
              <dt className="text-2xl font-bold text-gray-900 dark:text-white">PDF</dt>
              <dd className="text-sm text-gray-500 dark:text-gray-400">Free download</dd>
            </div>
            <div>
              <dt className="text-2xl font-bold text-gray-900 dark:text-white">100%</dt>
              <dd className="text-sm text-gray-500 dark:text-gray-400">Actionable</dd>
            </div>
          </dl>
        </div>
      </section>

      {spotlight && (
        <section className="pb-12">
          <FeaturedEbookSpotlight ebook={spotlight} />
        </section>
      )}

      <section id="topics" className="py-12">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">eBook Topics</h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
            Playbooks organized by what performance marketers need most
          </p>
        </div>
        <TopicsGrid />
      </section>

      {featuredGrid.length > 0 && (
        <section className="py-12">
          <div className="mb-10">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Featured eBooks</h2>
            <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">Start with our most popular playbooks</p>
          </div>
          <Suspense fallback={<ResourceGridSkeleton type="featured" count={featuredGrid.length} />}>
            <ResourceGrid resources={featuredGrid} type="featured" category="ebook" />
          </Suspense>
        </section>
      )}

      <section id="ebooks" className="py-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">All eBooks</h2>
            <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">Complete collection of downloadable playbooks</p>
          </div>
          <Link
            href="/resources"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
          >
            Resource Center <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
        <Suspense fallback={<ResourceGridSkeleton type="default" count={3} />}>
          <ResourceGrid resources={ebooks} type="default" category="ebook" />
        </Suspense>
      </section>
    </div>
  )
}
